import { Injectable, inject, signal } from '@angular/core';
import { catchError, finalize, map, of, tap, throwError } from 'rxjs';
import { AssignedFloorRecord } from '@core/models/project.models';
import { ProjectService } from './project.service';

export interface FloorAssignment {
  /** Existing backend record id, omitted for assignments that only exist locally. */
  id?: number;
  /** Floor name assigned to the source page. */
  floorName: string;
  /** One-based page number from the uploaded plan document. */
  pageNumber: number;
  /** Human-readable page label shown in selection controls. */
  pageLabel: string;
  /** Scale string for this floor (e.g. "1/4\" = 1'-0\"" or "1:192"). */
  scaleAssigned?: string;
  /** How the scale was determined — from a predefined list or calibrated on the PDF. */
  scaleType?: 'standard' | 'calibrated';
}

export interface AvailableFloor {
  /** Stable floor option identifier. */
  id: string;
  /** Display name for the floor option. */
  name: string;
}

@Injectable({ providedIn: 'root' })
export class StepperFloorService {
  private readonly projectService = inject(ProjectService);

  readonly floorAssignments        = signal<FloorAssignment[]>([]);
  readonly submittedPageNumbers    = signal<number[]>([]);
  readonly assignedFloorsLoading   = signal(false);
  readonly deletingFloorAssignment = signal(false);
  readonly editingFloorAssignment  = signal(false);
  readonly step3DataLoaded         = signal(false);

  /** Adds or replaces a floor assignment in local state. */
  assign(assignment: FloorAssignment): void {
    this.floorAssignments.update(list => {
      const filtered = list.filter(a => a.pageNumber !== assignment.pageNumber);
      return [...filtered, assignment].sort((a, b) => a.pageNumber - b.pageNumber);
    });
  }

  /**
   * Populates signals from backend records.
   * Returns the scale string from the first entry so the caller can reconcile it.
   *
   * Empty server responses do not overwrite non-empty local state because the
   * backend cache can lag immediately after a user assigns floors.
   */
  hydrateFromEntries(entries: AssignedFloorRecord[]): string | undefined {
    const assignments: FloorAssignment[] = entries.map(entry => ({
      id:            entry.id,
      floorName:     entry.assigned_floor_name,
      pageNumber:    entry.page_no,
      pageLabel:     `Page ${entry.page_no}`,
      scaleAssigned: entry.scale_assigned || undefined,
      scaleType:     entry.scale_type     || undefined,
    }));

    if (entries.length > 0 || this.floorAssignments().length === 0) {
      this.floorAssignments.set(assignments.sort((a, b) => a.pageNumber - b.pageNumber));
      this.submittedPageNumbers.set(assignments.map(a => a.pageNumber));
    }

    return entries[0]?.scale_assigned || undefined;
  }

  removeLocally(pageNumber: number): void {
    this.floorAssignments.update(list => list.filter(a => a.pageNumber !== pageNumber));
    this.submittedPageNumbers.update(pages => pages.filter(p => p !== pageNumber));
  }

  delete$(pageNumber: number) {
    const assignment = this.floorAssignments().find(a => a.pageNumber === pageNumber);
    if (!assignment?.id) {
      this.removeLocally(pageNumber);
      return of(void 0);
    }
    this.deletingFloorAssignment.set(true);
    return this.projectService.deleteAssignedFloor(assignment.id).pipe(
      tap(() => this.removeLocally(pageNumber)),
      map(() => void 0),
      catchError(err => throwError(() => err)),
      finalize(() => this.deletingFloorAssignment.set(false)),
    );
  }

  edit$(id: number, pageNumber: number, newFloorName: string) {
    this.editingFloorAssignment.set(true);
    return this.projectService.editAssignedFloor(id, newFloorName).pipe(
      tap(() => {
        this.floorAssignments.update(list =>
          list.map(a => a.pageNumber === pageNumber ? { ...a, floorName: newFloorName } : a),
        );
      }),
      map(() => void 0),
      catchError(err => throwError(() => err)),
      finalize(() => this.editingFloorAssignment.set(false)),
    );
  }

  reset(): void {
    this.floorAssignments.set([]);
    this.submittedPageNumbers.set([]);
    this.assignedFloorsLoading.set(false);
    this.deletingFloorAssignment.set(false);
    this.editingFloorAssignment.set(false);
    this.step3DataLoaded.set(false);
  }
}
